import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../store/auth-context';
import { logout } from '../services/notification.services';
import { SUPER_ADMIN_ID } from '../constants';

/**
 * Custom hook for handling user logout
 * Centralizes logout logic to avoid duplication
 */
export const useLogout = () => {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogout = useCallback(async () => {
    try {
      // Store token before clearing context to use in logout API call
      const tokenToUse = authCtx.userData?.superAdminId === SUPER_ADMIN_ID 
        ? authCtx.userData.superAdminToken 
        : authCtx.token;
      
      // IMPORTANT: Clear auth context and local storage FIRST
      // This prevents useEffect hooks from triggering API calls after logout
      authCtx.logout();
      localStorage.clear();
      
      // Navigate to login immediately
      navigate('/login');
      
      // Call logout API in background (non-blocking)
      // If it fails, user is already logged out from the frontend
      try {
        await logout(tokenToUse);
      } catch (apiError) {
        // Silent fail - user is already logged out from frontend
        console.error('Logout API error (non-critical):', apiError);
      }
    } catch (error) {
      // Fallback: ensure state is cleared even if something goes wrong
      console.error('Logout error:', error);
      authCtx.logout();
      localStorage.clear();
      navigate('/login');
    }
  }, [authCtx, navigate]);

  return { handleLogout };
};
